import {
    ViewViewModel,
    ComponentManager,
    RADEnvironmentProxy,
} from 'radpresentation.htmlclient.defaultviewextensions';

import {
    IAgeSeries,
    ILandesverbandOption,
} from '@/Components/AgeChart/ONVAgeBarChartElement';
import { fetchAgeDistributionForLandesverbaende } from '@/Components/AgeChart/ageDistributionService';
// Registriert das KO-Binding "onvAgeBarChart" (geteilt mit der Startseite).
import '@/Components/AgeChart/onvAgeBarChartBinding';
import {
    fetchKursunterlagenDownloadOverview,
    IKursunterlagenDownloadRow
} from '@/Services/KursunterlagenDownload/kursunterlagenDownloadService';
import { fetchNewcomerStatusOverview, INewcomerStatusRow } from '@/Services/NewcomerStatus/newcomerStatusService';

/** Vorausgewählte Zeithorizonte (Jahre). */
const DEFAULT_HORIZONS = [3, 6];

/**
 * Wartezeit (ms), bevor nach einer Nutzerinteraktion (LV-Auswahl/Zeithorizont)
 * die Altersverteilung neu geladen wird. Verhindert, dass beim schnellen
 * An-/Abwählen mehrerer Landesverbände für jeden Klick eine Anfrage gesendet wird.
 */
const CHART_RELOAD_DEBOUNCE_MS = 2000;

interface IPrincipalLike {
    pdoId:    string;
    name?:    string;
    fullName?: string;
}

// ── ViewModel ─────────────────────────────────────────────────────────────────

/**
 * ViewModel der Statistik-Seite. Lädt die für den angemeldeten Benutzer sichtbaren
 * Landesverbände (Principals) und stellt EIN gemeinsames Altersverteilungs-Diagramm
 * bereit, in dem die ausgewählten Landesverbände verglichen werden. Neben Zeithorizont
 * und Altersgruppen-Breite kann der Nutzer also auch die Landesverbände wählen.
 */
export default class ONVStatisticViewModel extends ViewViewModel
{
    // ── Landesverband-Auswahl (Optionen für das Diagramm) ────────────────────────
    public readonly landesverbandOptions: KnockoutObservableArray<ILandesverbandOption>;
    public readonly isLoadingPrincipals:  KnockoutObservable<boolean>;
    public readonly principalsError:      KnockoutObservable<string | null>;
    /** True, wenn keine Landesverbände vorhanden sind. */
    public readonly hasNoLandesverbaende: KnockoutComputed<boolean>;

    // ── Diagrammzustand (eine kombinierte Grafik) ────────────────────────────────
    public readonly series:        KnockoutObservableArray<IAgeSeries>;
    public readonly isLoadingChart: KnockoutObservable<boolean>;
    public readonly chartError:     KnockoutObservable<string | null>;

    /** Aktuelle Auswahl (kein Observable nötig – das Diagramm hält den UI-Zustand selbst). */
    private selectedHorizons: number[]  = DEFAULT_HORIZONS.slice();
    private selectedLvIds:     string[] = [];

    /** debouncing timer (null = kein Reload ausstehend). */
    private chartReloadTimer: ReturnType<typeof setTimeout> | null = null;
    /** Laufende Nummer der Chart-Anfrage – verwirft verspätete Antworten älterer Anfragen. */
    private chartRequestId = 0;

    // ── Status-Tabellen (je Landesverband) ──────────────────────────────────────
    public readonly statusRows:        KnockoutObservableArray<INewcomerStatusRow>;
    public readonly isLoadingStatus:   KnockoutObservable<boolean>;
    public readonly statusError:       KnockoutObservable<string | null>;
    /** True, wenn die Statusabfrage abgeschlossen ist und keine Daten lieferte. */
    public readonly hasNoStatusRows:   KnockoutComputed<boolean>;

    // ── Download-Statistik (je Landesverband) ────────────────────────────────────
    public readonly downloadRows:        KnockoutObservableArray<IKursunterlagenDownloadRow>;
    public readonly isLoadingDownloads:  KnockoutObservable<boolean>;
    public readonly downloadsError:      KnockoutObservable<string | null>;
    /** True, wenn die Download-Abfrage abgeschlossen ist und keine Daten lieferte. */
    public readonly hasNoDownloadRows:   KnockoutComputed<boolean>;

    constructor(params: any)
    {
        super(params);

        this.landesverbandOptions = ko.observableArray<ILandesverbandOption>([]);
        this.isLoadingPrincipals  = ko.observable<boolean>(true);
        this.principalsError      = ko.observable<string | null>(null);

        this.hasNoLandesverbaende = this.defineComputed(() =>
            !this.isLoadingPrincipals() && this.principalsError() == null
            && this.landesverbandOptions().length === 0);

        this.series         = ko.observableArray<IAgeSeries>([]);
        this.isLoadingChart = ko.observable<boolean>(true);
        this.chartError     = ko.observable<string | null>(null);

        this.statusRows      = ko.observableArray<INewcomerStatusRow>([]);
        this.isLoadingStatus = ko.observable<boolean>(true);
        this.statusError     = ko.observable<string | null>(null);

        this.hasNoStatusRows = this.defineComputed(() =>
            !this.isLoadingStatus() && this.statusError() == null && this.statusRows().length === 0);

        this.downloadRows      = ko.observableArray<IKursunterlagenDownloadRow>([]);
        this.isLoadingDownloads = ko.observable<boolean>(true);
        this.downloadsError     = ko.observable<string | null>(null);

        this.hasNoDownloadRows = this.defineComputed(() =>
            !this.isLoadingDownloads() && this.downloadsError() == null && this.downloadRows().length === 0);

        this.loadPrincipals();
        this.loadStatusOverview();
        this.loadDownloadOverview();
    }

    // ── Download-Statistik laden ─────────────────────────────────────────────────

    private loadDownloadOverview(): void
    {
        this.isLoadingDownloads(true);
        this.downloadsError(null);

        fetchKursunterlagenDownloadOverview().then(
            (overview) => {
                if (this.isDisposed) return;
                this.downloadRows(overview?.rows ?? []);
                this.isLoadingDownloads(false);
            },
            () => {
                if (this.isDisposed) return;
                this.downloadRows([]);
                this.downloadsError('Die Download-Statistik konnte nicht geladen werden.');
                this.isLoadingDownloads(false);
            },
        );
    }

    // ── Statusuebersicht laden ──────────────────────────────────────────────────

    private loadStatusOverview(): void
    {
        this.isLoadingStatus(true);
        this.statusError(null);

        fetchNewcomerStatusOverview().then(
            (overview) => {
                if (this.isDisposed) return;
                this.statusRows(overview?.rows ?? []);
                this.isLoadingStatus(false);
            },
            () => {
                if (this.isDisposed) return;
                this.statusRows([]);
                this.statusError('Die Statusübersicht konnte nicht geladen werden.');
                this.isLoadingStatus(false);
            },
        );
    }

    // ── Landesverbände laden ──────────────────────────────────────────────────

    private loadPrincipals(): void
    {
        this.isLoadingPrincipals(true);
        this.principalsError(null);

        RADEnvironmentProxy.getInstance().getPrincipals().then(
            (principals: any[]) => {
                if (this.isDisposed) return;

                // getPrincipals liefert zur Laufzeit Objekte mit pdoId/name/fullName
                // (vgl. LandesverbandWechselnDialogViewModel) – daher lose typisiert.
                const list = (principals ?? []) as IPrincipalLike[];
                const options = list
                    .filter(p => p.pdoId != null)
                    .map<ILandesverbandOption>(p => ({
                        id:   p.pdoId,
                        name: p.fullName || p.name || p.pdoId,
                    }));

                this.landesverbandOptions(options);
                // Standard: alle Landesverbände auswählen (deckungsgleich mit der
                // Vorauswahl der Web-Component).
                this.selectedLvIds = options.map(o => o.id);
                this.isLoadingPrincipals(false);

                this.reloadChart();
            },
            () => {
                if (this.isDisposed) return;
                this.landesverbandOptions([]);
                this.selectedLvIds = [];
                this.principalsError('Die Landesverbände konnten nicht geladen werden.');
                this.isLoadingPrincipals(false);
                this.isLoadingChart(false);
            },
        );
    }

    // ── Nutzerinteraktion (Events der Web-Component) ─────────────────────────────

    /** Wird vom KO-Binding aufgerufen, wenn der Nutzer die Zeithorizonte ändert. */
    public onHorizonsChange(horizons: number[]): void
    {
        this.selectedHorizons = horizons.slice().sort((a, b) => a - b);
        this.scheduleChartReload();
    }

    /** Wird vom KO-Binding aufgerufen, wenn der Nutzer die Landesverband-Auswahl ändert. */
    public onLandesverbaendeChange(landesverbandIds: string[]): void
    {
        this.selectedLvIds = landesverbandIds.slice();
        this.scheduleChartReload();
    }

    // ── Daten laden ──────────────────────────────────────────────────────────────

    /**
     * Stößt den Chart-Reload entprellt an: Jede weitere Interaktion innerhalb der
     * Wartezeit startet den Timer neu, sodass erst nach einer kurzen Ruhephase
     * genau EINE Anfrage gesendet wird.
     */
    private scheduleChartReload(): void
    {
        if (this.chartReloadTimer != null)
            clearTimeout(this.chartReloadTimer);

        // Sofortiges Feedback: Ladeindikator schon während der Wartezeit anzeigen.
        this.isLoadingChart(true);
        this.chartError(null);

        this.chartReloadTimer = setTimeout(() => {
            this.chartReloadTimer = null;
            if (this.isDisposed) return;
            this.reloadChart();
        }, CHART_RELOAD_DEBOUNCE_MS);
    }

    private reloadChart(): void
    {
        const requestId = ++this.chartRequestId;

        const horizons = this.selectedHorizons.slice().sort((a, b) => a - b);
        const lvIds    = this.selectedLvIds.slice();

        if (horizons.length === 0 || lvIds.length === 0) {
            this.series([]);
            this.isLoadingChart(false);
            this.chartError(null);
            return;
        }

        this.isLoadingChart(true);
        this.chartError(null);

        fetchAgeDistributionForLandesverbaende(horizons, lvIds).then(
            (parsed) => {
                if (this.isDisposed || requestId !== this.chartRequestId) return;
                this.series(parsed?.series ?? []);
                this.isLoadingChart(false);
            },
            () => {
                if (this.isDisposed || requestId !== this.chartRequestId) return;
                this.series([]);
                this.chartError('Die Altersverteilung konnte nicht geladen werden.');
                this.isLoadingChart(false);
            },
        );
    }
}

ComponentManager.setViewModel('ONVStatisticViewModel', ONVStatisticViewModel, 'ONVHTMLViewExtension');
